// X99 (Grant 2026-10-21): the admin Memberships & passes page as tabs, in the route query (?tab=, router.replace),
// like the Bookings page. Pure helpers: the tabs, where each old "More" item went, and the Current memberships &
// passes list (one row per membership or pass): the route query ⇄ its state, the state → the memberships list /
// download filters (ONE filters object for both), and the words of a row.
import {pickTab} from './pillTabs.js';
import {MEMBERSHIP_STATUS_INT} from './memberships.js';

/**
 * The tabs, in order. The types list is first and the default. X110 (R46 M2: the page title, the tab and the panel
 * heading all said "Memberships & passes"): under the page's own title the tabs say What you offer (the memberships
 * and passes people can take) and Members & pass holders (who holds one now; X113, Grant: "Current" alone did not say
 * current what); neither repeats the title.
 */
export const MEMBERSHIPS_TABS = Object.freeze([
  {value: 'types', title: 'What you offer', icon: 'mdi-card-account-details-outline'},
  // Grant: one row per current membership or pass (CurrentMembershipsList).
  // X108/X110: its list draws its own actions row (Add someone / Download), so the panel adds no head.
  {value: 'current', title: 'Members & pass holders', icon: 'mdi-badge-account-horizontal-outline', ownHead: true},
  {value: 'settings', title: 'Settings', icon: 'mdi-cog-outline'},
  {value: 'website', title: 'Widgets', icon: 'mdi-code-tags'}, // X146: the value stays so ?tab=website links still work
]);

export function pickMembershipsTab(query) {
  return pickTab(MEMBERSHIPS_TABS, query, 'types');
}

/**
 * Where each item of the old header "More" menu went (X99). `tab` = it became (part of) that tab; `action` = a one-off
 * action in that tab's header. The More menu is gone.
 */
export const MORE_ITEMS_MOVED = Object.freeze([
  {title: 'Put it on your website', kind: 'tab', tab: 'website', why: 'A place: the Join widget and its code, as on the Bookings page.'},
  {title: 'Who can see health details', kind: 'tab', tab: 'settings', why: 'A club setting; it sits with who can see the players.'},
  {title: 'Download medical answers', kind: 'action', tab: 'settings', why: 'A one-off, logged download that belongs with the health setting.'},
  {title: 'Import', kind: 'action', tab: 'current', why: 'A one-off action that adds memberships and passes; in the list\'s ⋮.'},
]);

// ── Current memberships & passes ──
export const CURRENT_PAGE_SIZE = 25;

/** Status pill: both are current (MembershipRules.IsCurrent: Active or PastDue inside the term). */
export const CURRENT_STATUS_OPTIONS = Object.freeze([
  {value: 'paidUp', label: 'Paid up', status: MEMBERSHIP_STATUS_INT.Active},
  {value: 'paymentFailed', label: 'Payment failed, retrying', status: MEMBERSHIP_STATUS_INT.PastDue},
]);

/** "Renews or ends within" pill (the API's renewsOn is the current term's end, for renewing and ending ones alike). */
export const RENEWS_OPTIONS = Object.freeze([
  {value: '7', label: 'Renews or ends within 7 days'},
  {value: '30', label: 'Renews or ends within 30 days'},
  {value: '90', label: 'Renews or ends within 90 days'},
]);

export const CURRENT_KEYS = Object.freeze(['types', 'statuses', 'renews', 'q', 'page']);

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const list = (v) => (Array.isArray(v) ? v : (v == null || v === '' ? [] : String(v).split(','))).map(x => String(x).trim()).filter(Boolean);
const STATUS_VALUES = new Set(CURRENT_STATUS_OPTIONS.map(o => o.value));
const RENEWS_VALUES = new Set(RENEWS_OPTIONS.map(o => o.value));

/** The list's state from the route query (unknown values dropped). */
export function currentStateFromQuery(query = {}) {
  const page = parseInt(String(query.page ?? '1'), 10);
  return {
    types: [...new Set(list(query.types).filter(x => GUID.test(x)).map(x => x.toLowerCase()))],
    statuses: [...new Set(list(query.statuses).filter(x => STATUS_VALUES.has(x)))],
    renews: RENEWS_VALUES.has(String(query.renews ?? '')) ? String(query.renews) : '',
    q: query.q == null ? '' : String(query.q),
    page: Number.isFinite(page) && page > 1 ? page : 1,
  };
}

/** The route query for a state: the list's keys replaced (defaults left out), every other key (tab, kind…) kept. */
export function currentQueryFromState(state, query = {}) {
  const next = {...query};
  for (const k of CURRENT_KEYS) delete next[k];
  if (state.types?.length) next.types = state.types.join(',');
  if (state.statuses?.length) next.statuses = state.statuses.join(',');
  if (state.renews) next.renews = state.renews;
  if (state.q && state.q.trim()) next.q = state.q.trim();
  if (state.page > 1) next.page = String(state.page);
  return next;
}

export function sameQuery(a = {}, b = {}) {
  const norm = (q) => JSON.stringify(Object.keys(q).sort().filter(k => q[k] != null).map(k => [k, String(q[k])]));
  return norm(a) === norm(b);
}

export function hasCurrentFilters(state) {
  return !!(state.types.length || state.statuses.length || state.renews || state.q.trim());
}

/** The filters the memberships list AND its download take (`filters[...]`): current only, then the pills. */
export function currentListFilters(clubId, state, now = new Date()) {
  const filters = {clubIds: [clubId], current: true};
  if (state.types.length) filters.membershipTypeIds = state.types;
  if (state.statuses.length) filters.statuses = CURRENT_STATUS_OPTIONS.filter(o => state.statuses.includes(o.value)).map(o => o.status);
  if (state.renews) {
    const end = new Date(now.getTime() + Number(state.renews) * 86400000);
    filters.renewsOn = [now.toISOString(), end.toISOString()];
  }
  if (state.q.trim()) filters.keywords = state.q.trim();
  return filters;
}

/** The scope line of the download dialog: "Current · Senior + Junior · renews or ends within 30 days · matching “jo”". */
export function currentScopeText(state, typeNames = []) {
  const bits = ['Current'];
  if (state.types.length) bits.push(typeNames.length === state.types.length && typeNames.length <= 2 ? typeNames.join(' + ') : `${state.types.length} types`);
  if (state.statuses.length === 1) bits.push(CURRENT_STATUS_OPTIONS.find(o => o.value === state.statuses[0])?.label.toLowerCase());
  if (state.renews) bits.push(`renews or ends within ${state.renews} days`);
  if (state.q.trim()) bits.push(`matching “${state.q.trim()}”`);
  return bits.filter(Boolean).join(' · ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "3 Sep" (this year) / "3 Sep 2027", from the date part the API sends (a calendar day, never shifted by zones). */
export function dayWords(value, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!m) return '';
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return `${d} ${MONTHS[mo - 1]}${y === now.getFullYear() ? '' : ' ' + y}`;
}

const STATUS_CHIPS = {
  startsSoon: {label: 'Starts soon', cls: 'tl-chip--brand'},
  current: {label: 'Current', cls: 'tl-chip--ok'},
  ending: {label: 'Ending', cls: 'tl-chip--warn'},
  paymentFailed: {label: 'Payment failed', cls: 'cm-chip--error'},
};

/**
 * One row's words. Holder; the type and Membership / Pass; the status chip; the date ("Renews 3 Sep" / "Ends 31 Aug" /
 * "Starts 22 Oct"); who pays only when it is someone else; the group (a link that opens it).
 */
export function currentRowWords(m, now = new Date()) {
  const holder = `${m.memberFirstName ?? ''} ${m.memberLastName ?? ''}`.trim() || 'Someone';
  const kind = m.displayAs === 'Pass' ? 'Pass' : 'Membership';
  const statusKey = m.statusKey && STATUS_CHIPS[m.statusKey] ? m.statusKey : (m.status === 'PastDue' ? 'paymentFailed' : m.cancelAtPeriodEnd ? 'ending' : 'current');
  let date = '';
  if (m.startsOn) date = `Starts ${dayWords(m.startsOn, now)}`;
  else if (m.renewsOn) date = `Renews ${dayWords(m.renewsOn, now)}`;
  else if (m.lastDay) date = `Ends ${dayWords(m.lastDay, now)}`;
  const payer = ['other', 'guest'].includes(m.paidByKind) && m.paidByLine ? m.paidByLine : '';
  return {
    holder,
    email: m.memberEmail || '',
    type: m.membershipTypeName || '',
    kind,
    kindIcon: kind === 'Pass' ? 'mdi-ticket-confirmation-outline' : 'mdi-card-account-details-outline',
    status: STATUS_CHIPS[statusKey].label,
    statusCls: STATUS_CHIPS[statusKey].cls,
    date,
    payer,
    group: m.membershipGroupId && !m.leftGroupAt ? (m.groupLine || m.groupName || 'Group') : '',
    groupId: m.membershipGroupId && !m.leftGroupAt ? m.membershipGroupId : null,
  };
}

/** What PlayerMembershipsDialog (the Players page's detail) takes for a row's holder. */
export function holderAsPlayer(m) {
  return {
    id: m.memberPlayerId,
    firstName: m.memberFirstName ?? '',
    lastName: m.memberLastName ?? '',
    fullName: `${m.memberFirstName ?? ''} ${m.memberLastName ?? ''}`.trim(),
    email: m.memberEmail ?? null,
    userId: m.memberUserId ?? null,
    membership: m.paidByLine ? {paidByLine: m.paidByLine, paidByKind: m.paidByKind} : null,
  };
}

/** Nobody at all vs. nobody for these filters. */
export function currentEmptyState({total, filtered}) {
  if (total > 0) return null;
  return filtered
      ? {key: 'filtered', title: 'No memberships or passes match these filters'}
      : {key: 'none', title: 'No current memberships or passes yet'};
}

/**
 * X100: a type's second line on a phone (below 600px), where the types table is one column like the
 * All bookings rows (no sideways scroll): price, Membership or Pass, how many current, any waiting.
 * The status chip sits beside it.
 */
export function typePhoneLine(type, kindLabel) {
  if (!type) return '';
  const price = type.priceSummary || (type.isFree ? 'Free' : '');
  const waiting = type.pendingApplicationCount ? `${type.pendingApplicationCount} waiting` : null;
  return [price, kindLabel, `${type.currentCount ?? 0} current`, waiting].filter(Boolean).join(' · ');
}

/**
 * X101 (R40 L2): the Current tab's type filter options. A name alone when it's unique; same-named types get what tells
 * them apart, shortest first: the price ("Senior · £240.00 a year"), then the kind (Membership / Pass), then a number.
 * Archived ones always say "(archived)".
 */
export function typeFilterOptions(types, kindLabel = () => '') {
  const rows = (types ?? []).map(t => ({
    value: String(t.id).toLowerCase(),
    base: t.archivedAt ? `${t.name} (archived)` : String(t.name ?? ''),
    price: t.priceSummary || (t.isFree ? 'Free' : ''),
    kind: kindLabel(t.displayAs) || '',
  }));
  const label = rows.map(r => r.base);
  const clash = (i) => label.some((l, j) => j !== i && l === label[i]);
  const extend = (pick) => {
    const dup = label.map((_, i) => clash(i));
    rows.forEach((r, i) => { if (dup[i] && pick(r)) label[i] = `${label[i]} · ${pick(r)}`; });
  };
  extend(r => r.price);
  extend(r => r.kind);
  const seen = {};
  const dup = label.map((_, i) => clash(i));
  label.forEach((l, i) => { if (dup[i]) { seen[l] = (seen[l] ?? 0) + 1; label[i] = `${l} · ${seen[l]}`; } });
  return rows.map((r, i) => ({value: r.value, label: label[i]}));
}

/** X101 (R40): the member detail dialog says "Current" where the status chip says "Active", as the Current list does. */
export function listStatusChip(chip) {
  if (!chip) return chip;
  return chip.label === 'Active' ? {...chip, label: 'Current'} : chip;
}

const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const LONG_DATE = new RegExp(`\\b(\\d{1,2}) (${LONG_MONTHS.join('|')}) (\\d{4})\\b`, 'g');

/** X101 (R40): the API's sentences ("Renews 18 October 2027") in the dialog's one date style ("Renews Mon 18 Oct 2027"). */
export function shortDatesInLine(text, format) {
  if (!text || typeof format !== 'function') return text;
  return String(text).replace(LONG_DATE, (all, d, mon, y) => {
    const iso = `${y}-${String(LONG_MONTHS.indexOf(mon) + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00:00Z`;
    return format(iso) || all;
  });
}
